import React from 'react'
import { ContextualMenu, type ContextualMenuItem } from '../ContextualMenu'
import ActionButton from './ActionButton.component'
import { ActionButtonIcons } from './ActionButtons.icons'
import type {
  ActionButtonConfig,
  ActionButtonsProps,
} from './ActionButtons.types'
import {
  ACTION_BUTTON_DEFAULT_CREATE_INVOICE_VARIANT,
  ACTION_BUTTON_DEFAULT_DELETE_VARIANT,
  ACTION_BUTTON_DEFAULT_EDIT_VARIANT,
  ACTION_BUTTON_DEFAULT_PAYMENT_VARIANT,
  ACTION_BUTTON_DEFAULT_PRINT_VARIANT,
  ACTION_BUTTON_DEFAULT_REFUND_VARIANT,
  ACTION_BUTTON_DEFAULT_VIEW_NOTES_VARIANT,
  ACTION_BUTTON_DEFAULT_VIEW_VARIANT,
} from './ActionButtons.types'
import './ActionButtons.css'

const buildDefaultActions = (
  props: ActionButtonsProps,
): ActionButtonConfig[] => {
  const {
    onView,
    onEdit,
    onDelete,
    showView = true,
    showEdit = true,
    showDelete = true,
    disableEdit = false,
    disableDelete = false,
    viewVariant = ACTION_BUTTON_DEFAULT_VIEW_VARIANT,
    editVariant = ACTION_BUTTON_DEFAULT_EDIT_VARIANT,
    deleteVariant = ACTION_BUTTON_DEFAULT_DELETE_VARIANT,
    onPayment,
    disablePayment = false,
    paymentVariant = ACTION_BUTTON_DEFAULT_PAYMENT_VARIANT,
    onPrint,
    printVariant = ACTION_BUTTON_DEFAULT_PRINT_VARIANT,
    onRefund,
    disableRefund = false,
    refundVariant = ACTION_BUTTON_DEFAULT_REFUND_VARIANT,
    onCreateInvoice,
    disableCreateInvoice = false,
    createInvoiceVariant = ACTION_BUTTON_DEFAULT_CREATE_INVOICE_VARIANT,
    onViewNotes,
    disableViewNotes = false,
    viewNotesVariant = ACTION_BUTTON_DEFAULT_VIEW_NOTES_VARIANT,
  } = props

  const actions: ActionButtonConfig[] = []

  if (showView && onView) {
    actions.push({
      id: 'view',
      label: 'View',
      icon: ActionButtonIcons.view(),
      onClick: onView,
      tone: 'primary',
      variant: viewVariant,
    })
  }

  if (showEdit && onEdit) {
    actions.push({
      id: 'edit',
      label: 'Edit',
      icon: ActionButtonIcons.edit(),
      onClick: onEdit,
      tone: 'primary',
      variant: editVariant,
      disabled: disableEdit,
    })
  }

  if (onPayment) {
    actions.push({
      id: 'payment',
      label: 'Create payment',
      icon: ActionButtonIcons.payment(),
      onClick: onPayment,
      tone: 'success',
      variant: paymentVariant,
      disabled: disablePayment,
    })
  }

  if (onPrint) {
    actions.push({
      id: 'print',
      label: 'Print',
      icon: ActionButtonIcons.print(),
      onClick: onPrint,
      tone: 'primary',
      variant: printVariant,
    })
  }

  if (onRefund) {
    actions.push({
      id: 'refund',
      label: 'Apply adjustment',
      icon: ActionButtonIcons.refund(),
      onClick: onRefund,
      tone: 'primary',
      variant: refundVariant,
      disabled: disableRefund,
    })
  }

  if (onCreateInvoice) {
    actions.push({
      id: 'create-invoice',
      label: 'Create invoice',
      icon: ActionButtonIcons.document(),
      onClick: onCreateInvoice,
      tone: 'primary',
      variant: createInvoiceVariant,
      disabled: disableCreateInvoice,
    })
  }

  if (onViewNotes) {
    actions.push({
      id: 'view-notes',
      label: 'View notes',
      icon: ActionButtonIcons.viewNotes(),
      onClick: onViewNotes,
      tone: 'primary',
      variant: viewNotesVariant,
      disabled: disableViewNotes,
    })
  }

  if (showDelete && onDelete) {
    actions.push({
      id: 'delete',
      label: 'Delete',
      icon: ActionButtonIcons.delete(),
      onClick: onDelete,
      tone: 'danger',
      variant: deleteVariant,
      disabled: disableDelete,
    })
  }

  return actions
}

const buildMenuItems = (props: ActionButtonsProps): ContextualMenuItem[] => {
  const {
    onDuplicate,
    onPin,
    isPinned = false,
    onPhone,
    onMail,
    onMessage,
    moreItems = [],
  } = props

  const items: ContextualMenuItem[] = []

  if (onDuplicate) {
    items.push({
      id: 'duplicate',
      label: 'Duplicate',
      icon: ActionButtonIcons.duplicate(),
      onSelect: onDuplicate,
    })
  }

  if (onPin) {
    items.push({
      id: 'pin',
      label: isPinned ? 'Unpin' : 'Pin',
      icon: ActionButtonIcons.pin(),
      onSelect: onPin,
    })
  }

  if (onPhone) {
    items.push({
      id: 'phone',
      label: 'Call',
      icon: ActionButtonIcons.phone(),
      onSelect: onPhone,
    })
  }

  if (onMail) {
    items.push({
      id: 'mail',
      label: 'Email',
      icon: ActionButtonIcons.mail(),
      onSelect: onMail,
    })
  }

  if (onMessage) {
    items.push({
      id: 'message',
      label: 'Message',
      icon: ActionButtonIcons.message(),
      onSelect: onMessage,
    })
  }

  return [...items, ...moreItems]
}

const ActionButtons: React.FC<ActionButtonsProps> = (props) => {
  const {
    actions,
    middle,
    size = 'md',
    darkMode = false,
    className = '',
    previewOpen,
  } = props

  const resolvedActions = actions ?? buildDefaultActions(props)
  const menuItems = actions ? [] : buildMenuItems(props)

  const classNames = [
    'ds-action-buttons',
    darkMode ? 'ds-action-buttons--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classNames}>
      {resolvedActions.map((action) => (
        <ActionButton
          key={action.id}
          size={size}
          {...action}
          darkMode={darkMode}
        />
      ))}
      {middle}
      {menuItems.length > 0 ? (
        <ContextualMenu
          items={menuItems}
          ariaLabel="More actions"
          className="ds-action-buttons__more"
          darkMode={darkMode}
          previewState={previewOpen ? 'open' : undefined}
        />
      ) : null}
    </div>
  )
}

export default ActionButtons
